import React, { useEffect, useState, useContext } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuthFetch } from "../../hooks/useAuthFetch";
import { DeleteConfirm } from "../../components/modal/deleteConfirm";
import { TrashIcon, PlusIcon, UserIcon } from "@heroicons/react/24/outline";
import { Button } from "../../components/button";
import { PageHeading } from "../../components/pageHeading";
import { SuccessToast } from "../../components/toast/success";
import { AuthContext } from "../../contexts/AuthContext";
import { CustomerDetails } from "./details";
import { Table } from "../../components/table";

function Customers() {
  const { authContext, fetchUserData } = useContext(AuthContext);
  const orgId = authContext.organization.orgId;
  const authFetch = useAuthFetch();
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedDeleteItem, setSelectedDeleteItem] = useState({
    identifier: "",
    id: "",
  });
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isAddMode, setIsAddMode] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);

  const toggleState = (setState, state) => {
    setState(!state);
  };

  const handleDeleteClick = (identifier, id) => {
    setSelectedDeleteItem({ identifier, id });
    toggleState(setIsDeleteOpen, isDeleteOpen);
  };

  const handleAddMode = (id) => {
    setSelectedCustomerId(id);
    setIsAddMode(false);
    toggleState(setIsDetailsOpen, isDetailsOpen);
  };

  // Deep link: /customers/?open=<id> opens that customer's side sheet.
  // The param is removed once consumed so closing the sheet doesn't reopen it.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const openId = searchParams.get("open");
    if (openId) {
      setSelectedCustomerId(openId);
      setIsAddMode(false);
      setIsDetailsOpen(true);
      searchParams.delete("open");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  /// Delete customer ///
  const deleteCustomer = (customerId) => {
    return authFetch(
      `${process.env.REACT_APP_API_URL}/v1/customers/delete/${customerId}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
      }
    ).then((response) => response.json());
  };

  const [deleteSuccess, setDeleteSuccess] = useState({
    displaying: false,
    message: "Customer has been deleted",
  });

  const handleDeleteSuccess = () => {
    fetchCustomers();

    setDeleteSuccess({
      displaying: true,
      message: "Customer has been deleted",
    });
    setTimeout(() => {
      setDeleteSuccess({ displaying: false, message: deleteSuccess.message });
    }, 3000);
  };

  const [fetchFlag, setFetchFlag] = useState(false);

  const handleSuccess = async () => {
    setIsLoading(true);
    setFetchFlag(!fetchFlag);
    await fetchUserData();
  };

  // Fetch customers when orgId changes
  useEffect(() => {
    if (orgId) {
      setIsLoading(true);
      fetchCustomers();
    }
  }, [orgId]);

  // Fetch customers when fetchFlag changes
  useEffect(() => {
    if (orgId) {
      fetchCustomers();
    }
  }, [fetchFlag]);

  /// Fetch customers ///
  const [backendData, setBackendData] = useState([]);
  const fetchCustomers = async () => {
    if (!orgId) return;

    const fetchPromise = authFetch(
      `${process.env.REACT_APP_API_URL}/v1/customers`
    )
      .then(async (response) => {
        if (response.ok) {
          const data = await response.json();
          setBackendData(data); // Update state with the fetched data
        } else {
          // Handle HTTP errors e.g., 404, 500 etc.
          console.error("HTTP Error: ", response.statusText);
          // Optionally, update state to display an error message
        }
      })
      .catch((error) => {
        // Handle network errors, parsing errors, etc.
        console.error("Fetching error: ", error.message);
        // Optionally, update state to display an error message
      });

    const timerPromise = new Promise((resolve) => setTimeout(resolve, 500));

    Promise.all([fetchPromise, timerPromise]).then(() => {
      setIsLoading(false);
    });
  };

  /// Table columns ///
  const columns = React.useMemo(
    () => [
      {
        Header: "Customer name",
        accessor: "customer_name",
        // Use a custom cell to render the link
        Cell: ({ row }) => (
          <div>
            {row.original.logo_url && row.original.logo_url.length > 0 ? (
              <img
                src={row.original.logo_url}
                alt=""
                className="mr-3 h-10 w-10 p-1 object-contain bg-white rounded-lg inline-block align-middle"
              />
            ) : (
              <span className="mr-2 w-10 h-10 rounded-full bg-gray-200 inline-block align-middle"></span>
            )}
            <a
              onClick={() => handleAddMode(row.original.id)}
              className="text-gray-800 dark:text-gray-100 hover:text-gray-900 dark:hover:text-white hover:underline capitalize cursor-pointer"
            >
              {row.values.customer_name}
            </a>
          </div>
        ),
      },
      {
        Header: "Contact name",
        accessor: "contact_name",
      },
      {
        Header: "Contact number",
        accessor: "contact_phone",
      },
      // Add a new column for the delete action
      {
        Header: "",
        id: "delete",
        accessor: (str) => "delete",
        Cell: ({ row }) => (
          <button
            onClick={() =>
              handleDeleteClick(row.original.customer_name, row.original.id)
            }
            className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors duration-150"
          >
            <TrashIcon className="h-4 w-4 shrink-0" />
          </button>
        ),
      },
    ],
    []
  );

  /// Toggle side sheet ///
  const toggleDetailsOpen = () => {
    if (isDetailsOpen) {
      setSelectedCustomerId(null);
    }
    setIsDetailsOpen(!isDetailsOpen);
  };

  return (
    <div>
      <main className="py-6">
        <div className="px-4 sm:px-6 lg:px-6">
          <PageHeading title="Customers">
            <Button
              icon={PlusIcon}
              onClick={() => {
                setIsAddMode(true);
                toggleState(setIsDetailsOpen, isDetailsOpen);
              }}
            >
              Create customer
            </Button>
          </PageHeading>

          <Table
            data={backendData}
            columns={columns}
            defaultSort="customer_name"
            isLoading={isLoading}
            emptyState={
              <div className="flex flex-col items-center justify-center h-72">
                <UserIcon className="h-10 w-10 text-gray-300 dark:text-gray-600" />
                <p className="mt-3 mb-4 text-sm text-gray-400 dark:text-gray-500 text-center">
                  No customers yet
                </p>
                <Button
                  onClick={() => {
                    setIsAddMode(true);
                    toggleState(setIsDetailsOpen, isDetailsOpen);
                  }}
                >
                  Create customer
                </Button>
              </div>
            }
          />
        </div>
      </main>
      <DeleteConfirm
        isOpen={isDeleteOpen}
        toggleOpen={() => toggleState(setIsDeleteOpen, isDeleteOpen)}
        selectedDeleteItem={selectedDeleteItem}
        handleDeleteSuccess={handleDeleteSuccess}
        onDelete={deleteCustomer}
      />
      <CustomerDetails
        isOpen={isDetailsOpen}
        toggleOpen={toggleDetailsOpen}
        selectedCustomerId={selectedCustomerId}
        isAddMode={isAddMode}
        onSuccess={handleSuccess}
      />
    </div>
  );
}

export { Customers };
